import { cls } from 'constants/cls';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  createAddressFailed,
  createAddressSuccess,
  deleteAddressFailed,
  deleteAddressSuccess,
  editAddressFailed,
  editAddressSuccess,
  postProfileSettingsFailed,
  postProfileSettingsSuccess,
} from 'redux/actions/userActions';
import safeAxios from 'shared/api/safeFetch';
import config from 'shared/config';
import { ClsItem } from 'types/clsType';
import { ErrorResponseType, PayloadErrorType } from 'types/reduxTypes';
import { SettingsModelType } from 'types/settingsType';
import { RootState } from 'types/stateTypes';
import { AcademicDegree, Author } from 'types/uploadType';
import {
  CreateAddressType,
  DeleteItemPayload,
  EditAddressType,
  EditEducationPayload,
  FindCitiesPayload,
  GetClsItemsPayload,
  GetUserProfileDataPayload,
  PostContactsPayload,
  PostEducationPayload,
  PostLaborActivityPayload,
  PostObjIdentifiersPayload,
  PostScientificInterestsPayload,
  deleteEducationFailed,
  deleteEducationSuccess,
  deleteLaborActivityFailed,
  deleteLaborActivitySuccess,
  editDegreesFailed,
  editDegreesSuccess,
  editLaborActivityFailed,
  editLaborActivitySuccess,
  findCitiesFailed,
  findCitiesSuccess,
  getActivityAreasFailed,
  getActivityAreasSuccess,
  getContacts,
  getContactsFailed,
  getContactsSuccess,
  getCountriesFailed,
  getCountriesSuccess,
  getDegrees,
  getDegreesFailed,
  getDegreesSuccess,
  getEducation,
  getEducationFailed,
  getEducationSuccess,
  getEducationTypesFailed,
  getEducationTypesSuccess,
  getLaborActivity,
  getLaborActivityFailed,
  getLaborActivitySuccess,
  getPositionsFailed,
  getPositionsSuccess,
  getScientificInterests,
  getScientificInterestsFailed,
  getScientificInterestsSuccess,
  getSettingsModelFailed,
  getSettingsModelSuccess,
  getUserAuthors,
  getUserAuthorsFailed,
  getUserAuthorsSuccess,
  postContactsFailed,
  postContactsSuccess,
  postDegreesFailed,
  postDegreesSuccess,
  postEducationFailed,
  postEducationSuccess,
  postLaborActivityFailed,
  postLaborActivitySuccess,
  postObjIdentifiersFailed,
  postObjIdentifiersSuccess,
  postScientificInterestsFailed,
  postScientificInterestsSuccess,
  searchOrganizationFailed,
  searchOrganizationSuccess,
} from '../actions/profileActions';
import { ActionType } from '../actions/themeActions';
import {
  CREATE_ADDRESS,
  DELETE_ADDRESS,
  DELETE_EDUCATION,
  DELETE_LABOR_ACIVITY,
  EDIT_ADDRESS,
  EDIT_DEGREES,
  EDIT_EDUCATION,
  EDIT_LABOR_ACIVITY,
  FIND_CITIES,
  FIND_CLS_ITEMS,
  GET_ACTIVITY_AREAS,
  GET_CONTACTS,
  GET_COUNTRIES,
  GET_DEGREES,
  GET_EDUCATION,
  GET_EDUCATION_TYPES,
  GET_LABOR_ACIVITY,
  GET_POSITIONS,
  GET_SCIENTIFIC_INTERESTS,
  GET_SETTINGS_MODEL,
  GET_USER_AUTHORS,
  POST_CONTACTS,
  POST_DEGREES,
  POST_EDUCATION,
  POST_LABOR_ACIVITY,
  POST_OBJ_IDENTIFIERS,
  POST_PROFILE_SETTINGS,
  POST_SCIENTIFIC_INTERESTS,
  SEARCH_ORGANIZATION,
} from '../constants/index';
import { findClsItemsHandler } from './clsSaga';

function* searchOrganizationHandler(action: ActionType<string>) {
  const searchString = action.payload;
  if (!searchString || searchString === '') {
    yield put(searchOrganizationSuccess([]));
    return;
  }
  const requestParams = new URLSearchParams({
    query: searchString,
  });
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/organization/find?${requestParams}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    yield put(searchOrganizationSuccess(data));
  } catch (error) {
    yield put(searchOrganizationFailed({ message: '' }));
  }
}

function* getLaborAcivityHandler(action: ActionType<string>) {
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${action.payload}/labor_activity`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    yield put(getLaborActivitySuccess(data));
  } catch (error) {
    yield put(getLaborActivityFailed({ message: '' }));
  }
}

function* getEducationHandler(action: ActionType<string>) {
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${action.payload}/education`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    yield put(getEducationSuccess(data));
  } catch (error) {
    yield put(getEducationFailed({ message: '' }));
  }
}

function* getDegreesHandler(action: ActionType<string>) {
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${action.payload}/user_academic_degrees`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    yield put(getDegreesSuccess(data));
  } catch (error) {
    yield put(getDegreesFailed({ message: '' }));
  }
}

function* postDegreesHandler(action: ActionType<AcademicDegree[]>) {
  const { auth }: RootState = yield select();
  const { id: userId } = auth;
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${userId}/user_academic_degrees`, {
      body: action.payload,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    yield put(postDegreesSuccess(data));
    yield put(getDegrees(userId as string));
  } catch (error) {
    yield put(postDegreesFailed({ message: '' }));
  }
}

function* editDegreesHandler(action: ActionType<AcademicDegree[]>) {
  const { auth }: RootState = yield select();
  const { id: userId } = auth;
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${userId}/user_academic_degrees`, {
      body: action.payload,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
    });
    yield put(editDegreesSuccess(data));
    yield put(getDegrees(userId as string));
  } catch (error) {
    yield put(editDegreesFailed({ message: '' }));
  }
}

function* getContactsHandler(action: ActionType<string>) {
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${action.payload}/contact`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    yield put(getContactsSuccess(data));
  } catch (error) {
    yield put(getContactsFailed({ message: '' }));
  }
}

function* getScientificInterestsHandler(action: ActionType<string>) {
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${action.payload}/scientific_interest`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    yield put(getScientificInterestsSuccess(data));
  } catch (error) {
    yield put(getScientificInterestsFailed({ message: '' }));
  }
}

function* getUserAuthorsHandler({ payload }: ActionType<GetUserProfileDataPayload>) {
  try {
    const requestParams = new URLSearchParams({
      query: payload.userId,
    });
    const { data } = yield call(safeAxios, `${config.apiUrl}/author?${requestParams}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      method: 'GET',
    });
    yield put(getUserAuthorsSuccess(data));
  } catch (error) {
    yield put(getUserAuthorsFailed({ message: '' }));
  }
}

function* postLaborAcivityHandler(action: ActionType<PostLaborActivityPayload>) {
  const body = action.payload.items;
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${action.payload.userId}/labor_activity`, {
      body: body,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    yield put(postLaborActivitySuccess(data));
    yield put(getLaborActivity(action.payload.userId));
  } catch (error) {
    yield put(postLaborActivityFailed({ message: '' }));
  }
}

function* editLaborAcivityHandler(action: ActionType<PostLaborActivityPayload>) {
  const body = action.payload.items[0];
  try {
    const { data } = yield call(
      safeAxios,
      `${config.apiUrl}/user_profile/${action.payload.userId}/labor_activity/${action.payload.items[0]?.id}`,
      {
        body: body,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
      }
    );
    yield put(editLaborActivitySuccess(data));
    yield put(getLaborActivity(action.payload.userId));
  } catch (error) {
    yield put(editLaborActivityFailed({ message: '' }));
  }
}

function* postEducationHandler(action: ActionType<PostEducationPayload>) {
  const body = action.payload.items;
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${action.payload.userId}/education`, {
      body: body,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    yield put(postEducationSuccess(data));
    yield put(getEducation(action.payload.userId));
  } catch (error) {
    yield put(postEducationFailed({ message: '' }));
  }
}

function* editEducationHandler(action: ActionType<EditEducationPayload>) {
  const body = action.payload.items[0];
  try {
    const { data } = yield call(
      safeAxios,
      `${config.apiUrl}/user_profile/${action.payload.userId}/education/${body.id}`,
      {
        body: body,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
      }
    );
    yield put(postEducationSuccess(data));
    yield put(getEducation(action.payload.userId));
  } catch (error) {
    yield put(postEducationFailed({ message: '' }));
  }
}

function* postContactsHandler({
  payload: { userId, contactsToCommit, contactsToRemove },
}: ActionType<PostContactsPayload>) {
  try {
    const ids: string[] = [];

    const contactsToCreate = contactsToCommit.filter((e) => !e.id);
    if (contactsToCreate.length) {
      const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${userId}/contact`, {
        body: contactsToCreate,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });
      ids.push(data);
    }

    const contactsToUpdate = contactsToCommit.filter((e) => e.id);
    for (let i = 0; i < contactsToUpdate.length; i++) {
      const item = contactsToUpdate[i];
      const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${userId}/contact/${item.id}`, {
        body: item,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
      });
      ids.push(data);
    }

    for (let i = 0; i < contactsToRemove.length; i++) {
      const item = contactsToRemove[i];
      yield call(safeAxios, `${config.apiUrl}/user_profile/${userId}/contact/${item.id}`, {
        credentials: 'include',
        method: 'DELETE',
      });
    }

    yield put(postContactsSuccess(ids));
    yield put(getContacts(userId));
  } catch (error) {
    yield put(postContactsFailed({ message: '' }));
  }
}

function* postScientificInterestsHandler({
  payload: { userId, interestsToCommit },
}: ActionType<PostScientificInterestsPayload>) {
  try {
    const ids: string[] = [];

    const interestsToCreate = interestsToCommit.filter((e) => !e.id);
    if (interestsToCreate.length) {
      const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${userId}/scientific_interest`, {
        body: interestsToCreate,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });
      ids.push(data);
    }

    const interestsToUpdate = interestsToCommit.filter((e) => e.id);
    for (let i = 0; i < interestsToUpdate.length; i++) {
      const item = interestsToUpdate[i];
      const { data } = yield call(safeAxios, `${config.apiUrl}/user_profile/${userId}/scientific_interest/${item.id}`, {
        body: item,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
      });
      ids.push(data);
    }

    yield put(postScientificInterestsSuccess(ids));
    yield put(getScientificInterests(userId));
  } catch (error) {
    yield put(postScientificInterestsFailed({ message: '' }));
  }
}

function* postObjIdentifiersHandler({
  payload: {
    user: { userInfo },
    identifiersToCommit,
  },
}: ActionType<PostObjIdentifiersPayload>) {
  try {
    const ids: string[] = [];

    const needToCreateAuthor = identifiersToCommit.some((e) => !e.rObject);
    if (needToCreateAuthor) {
      const requestParams = new URLSearchParams({
        user_id: userInfo.id,
      });
      const { data } = yield call(safeAxios, `${config.apiUrl}/author?${requestParams}`, {
        body: {
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          middleName: userInfo.middleName,
        } as Author,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });

      identifiersToCommit.forEach((e) => {
        if (!e.rObject) e.rObject = `${cls.author}:${data}`;
      });
    }

    const identifiersToCreate = identifiersToCommit.filter((e) => !e.id);
    if (identifiersToCreate.length) {
      const { data } = yield call(safeAxios, `${config.apiUrl}/obj_identifier`, {
        body: identifiersToCreate,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });
      ids.push(data);
    }

    const identifiersToUpdate = identifiersToCommit.filter((e) => e.id);
    for (let i = 0; i < identifiersToUpdate.length; i++) {
      const item = identifiersToUpdate[i];
      const { data } = yield call(safeAxios, `${config.apiUrl}/obj_identifier/${item.id}`, {
        body: item,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
      });
      ids.push(data);
    }

    yield put(postObjIdentifiersSuccess(ids));
    yield put(getUserAuthors({ userId: userInfo.id }));
  } catch (error) {
    yield put(postObjIdentifiersFailed({ message: '' }));
  }
}

function* deleteLaborAcivityHandler(action: ActionType<DeleteItemPayload>) {
  try {
    const { data } = yield call(
      safeAxios,
      `${config.apiUrl}/user_profile/${action.payload.userId}/labor_activity/${action.payload.itemId}`,
      {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
      }
    );
    yield put(deleteLaborActivitySuccess(data));
    yield put(getLaborActivity(action.payload.userId));
  } catch (error) {
    yield put(deleteLaborActivityFailed({ message: '' }));
  }
}

function* deleteEducationHandler(action: ActionType<DeleteItemPayload>) {
  try {
    const { data } = yield call(
      safeAxios,
      `${config.apiUrl}/user_profile/${action.payload.userId}/education/${action.payload.itemId}`,
      {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
      }
    );
    yield put(deleteEducationSuccess(data));
    yield put(getEducation(action.payload.userId));
  } catch (error) {
    yield put(deleteEducationFailed({ message: '' }));
  }
}

function* getPositionsHandler({ payload: { lang } }: ActionType<GetClsItemsPayload>) {
  yield call(getClsItemsHandler, {
    cls: cls.position,
    failed: getPositionsFailed,
    lang,
    success: getPositionsSuccess,
  });
}

function* getActivityAreasHandler({ payload: { lang } }: ActionType<GetClsItemsPayload>) {
  yield call(getClsItemsHandler, {
    cls: cls.activityArea,
    failed: getActivityAreasFailed,
    lang,
    pageSize: 10000,
    success: getActivityAreasSuccess,
  });
}

function* getEducationTypesHandler({ payload: { lang } }: ActionType<GetClsItemsPayload>) {
  yield call(getClsItemsHandler, {
    cls: cls.educationType,
    failed: getEducationTypesFailed,
    lang,
    success: getEducationTypesSuccess,
  });
}

function* getCountriesHandler({ payload: { lang } }: ActionType<GetClsItemsPayload>) {
  yield call(getClsItemsHandler, {
    cls: cls.countries,
    failed: getCountriesFailed,
    lang,
    pageSize: 1000,
    success: getCountriesSuccess,
  });
}

function* findCitiesHandler({ payload: { lang, term } }: ActionType<FindCitiesPayload>) {
  yield call(findClsItemsHandler, {
    payload: {
      cls: cls.cities,
      failed: findCitiesFailed,
      lang,
      success: findCitiesSuccess,
      term,
    },
    type: FIND_CLS_ITEMS,
  });
}

function* getClsItemsHandler({
  cls,
  lang,
  page,
  pageSize,
  success,
  failed,
}: {
  cls: number;
  lang?: number;
  page?: number;
  pageSize?: number;
  success: (payload: ClsItem[]) => ActionType<ClsItem[]>;
  failed: (payload: PayloadErrorType) => ActionType<PayloadErrorType>;
}) {
  const requestParams: string[] = [];
  requestParams.push(`cls=${cls}`);
  if (lang) {
    requestParams.push(`lang=${lang}`);
  }
  if (page) {
    requestParams.push(`page=${page}`);
  }
  if (pageSize) {
    requestParams.push(`pageSize=${pageSize}`);
  }
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/cls/all?${requestParams.join('&')}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    yield put(success(data));
  } catch (error) {
    yield put(failed({ message: '' }));
  }
}

function* getSettingsHandler() {
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/settings/model`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    });
    yield put(getSettingsModelSuccess(data as unknown as SettingsModelType));
  } catch (error: unknown) {
    const err = error as ErrorResponseType;
    const message = (err.response && err.response.status) || err.message || 'getFollowingsError';
    yield put(getSettingsModelFailed({ message: message as unknown as string }));
  }
}

function* createAddressHandler({ payload }: ActionType<CreateAddressType>) {
  try {
    const { data } = yield call(safeAxios, `${config.apiUrl}/address`, {
      body: {
        cCity: payload.cCity?.uref,
        cCountry: payload.cCountry?.uref,
        cType: payload.cType,
        city: payload.city,
        rObject: payload.rObject,
      },
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    yield put(createAddressSuccess(data as unknown));
  } catch (error) {
    const err = error as ErrorResponseType;
    const message = (err.response && err.response.status) || err.message || 'createAddressError';
    yield put(createAddressFailed({ message }));
  }
}

function* editAddressHandler({ payload }: ActionType<EditAddressType>) {
  try {
    yield call(safeAxios, `${config.apiUrl}/address/${payload.id}`, {
      body: {
        cCity: payload.cCity?.uref,
        cCountry: payload.cCountry?.uref,
        cType: payload.cType,
        city: payload.city,
        id: payload.id,
        rObject: payload.rObject,
      },
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
    });

    yield put(editAddressSuccess(payload as unknown));
  } catch (error) {
    const err = error as ErrorResponseType;
    const message = (err.response && err.response.status) || err.message || 'createAddressError';
    yield put(editAddressFailed({ message }));
  }
}

function* deleteAddressHandler({ payload: addressId }: ActionType<CreateAddressType>) {
  try {
    yield call(safeAxios, `${config.apiUrl}/address/${addressId}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    });

    yield put(deleteAddressSuccess(addressId));
  } catch (error) {
    const err = error as ErrorResponseType;
    const message = (err.response && err.response.status) || err.message || 'deleteAddressError';
    yield put(deleteAddressFailed({ message }));
  }
}

function* postProfileSettingsHandler({ payload }: ActionType<KeyValue<string | boolean>>) {
  try {
    yield call(safeAxios, `${config.apiUrl}/user_profile/settings`, {
      body: payload,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'PATCH',
    });
    yield put(postProfileSettingsSuccess(payload));
  } catch (error) {
    const err = error as ErrorResponseType;
    const message = (err.response && err.response.status) || err.message || 'createAddressError';
    yield put(postProfileSettingsFailed({ message }));
  }
}

export default function* fetchProfileWatcher(): Generator {
  yield takeEvery(POST_PROFILE_SETTINGS, postProfileSettingsHandler);
  yield takeEvery(SEARCH_ORGANIZATION, searchOrganizationHandler);
  yield takeEvery(GET_POSITIONS, getPositionsHandler);
  yield takeEvery(GET_LABOR_ACIVITY, getLaborAcivityHandler);
  yield takeEvery(GET_EDUCATION, getEducationHandler);
  yield takeEvery(GET_CONTACTS, getContactsHandler);
  yield takeEvery(GET_DEGREES, getDegreesHandler);
  yield takeEvery(GET_SCIENTIFIC_INTERESTS, getScientificInterestsHandler);
  yield takeEvery(POST_LABOR_ACIVITY, postLaborAcivityHandler);
  yield takeEvery(POST_DEGREES, postDegreesHandler);
  yield takeEvery(EDIT_DEGREES, editDegreesHandler);
  yield takeEvery(EDIT_LABOR_ACIVITY, editLaborAcivityHandler);
  yield takeEvery(POST_EDUCATION, postEducationHandler);
  yield takeEvery(EDIT_EDUCATION, editEducationHandler);
  yield takeEvery(POST_CONTACTS, postContactsHandler);
  yield takeEvery(POST_SCIENTIFIC_INTERESTS, postScientificInterestsHandler);
  yield takeEvery(POST_OBJ_IDENTIFIERS, postObjIdentifiersHandler);
  yield takeEvery(DELETE_LABOR_ACIVITY, deleteLaborAcivityHandler);
  yield takeEvery(DELETE_EDUCATION, deleteEducationHandler);
  yield takeEvery(GET_ACTIVITY_AREAS, getActivityAreasHandler);
  yield takeEvery(GET_EDUCATION_TYPES, getEducationTypesHandler);
  yield takeEvery(GET_COUNTRIES, getCountriesHandler);
  yield takeEvery(FIND_CITIES, findCitiesHandler);
  yield takeEvery(GET_USER_AUTHORS, getUserAuthorsHandler);
  yield takeEvery(GET_SETTINGS_MODEL, getSettingsHandler);
  yield takeEvery(CREATE_ADDRESS, createAddressHandler);
  yield takeEvery(EDIT_ADDRESS, editAddressHandler);
  yield takeEvery(DELETE_ADDRESS, deleteAddressHandler);
}
